export interface IPasswordStrengthResult {
  color: PasswordStrengthColor;
  strength: PasswordStrengthPercent;
  strengthText: string;
  isAcceptable: boolean;
  suggestions: string[];
}

export enum PasswordStrengthColor {
  Red = 'red',
  Yellow = 'yellow',
  Green = 'green'
}

export enum PasswordStrengthPercent {
  VeryWeak = '20%',
  Weak = '40%',
  Fair = '60%',
  Good = '80%',
  Strong = '100%'
}

export const MIN_PASSWORD_LENGTH = 8;
export const MIN_PASSWORD_CHARACTER_TYPES = 3;

const MIN_ACCEPTABLE_SCORE = 3;
const MAX_FAILING_SCORE = 2;

type PasswordStrengthLevel = {
  description: string;
  color: PasswordStrengthColor;
  percent: PasswordStrengthPercent;
};

const STRENGTH_LEVELS: Record<number, PasswordStrengthLevel> = {
  0: {
    description: 'Very Weak',
    color: PasswordStrengthColor.Red,
    percent: PasswordStrengthPercent.VeryWeak
  },
  1: {
    description: 'Weak',
    color: PasswordStrengthColor.Red,
    percent: PasswordStrengthPercent.Weak
  },
  2: {
    description: 'Fair',
    color: PasswordStrengthColor.Yellow,
    percent: PasswordStrengthPercent.Fair
  },
  3: {
    description: 'Good',
    color: PasswordStrengthColor.Green,
    percent: PasswordStrengthPercent.Good
  },
  4: {
    description: 'Strong',
    color: PasswordStrengthColor.Green,
    percent: PasswordStrengthPercent.Strong
  }
};

const CHARACTER_TYPE_RULES = [
  { label: 'lowercase', test: (password: string) => /[a-z]/.test(password) },
  { label: 'uppercase', test: (password: string) => /[A-Z]/.test(password) },
  { label: 'numbers', test: (password: string) => /[0-9]/.test(password) },
  {
    label: 'special character',
    test: (password: string) => /[^a-zA-Z0-9]/.test(password)
  }
];

export const PASSWORD_POLICY_SUGGESTION =
  `Must contain at least ${MIN_PASSWORD_LENGTH} characters in length and at least ` +
  `${MIN_PASSWORD_CHARACTER_TYPES} of the following ${CHARACTER_TYPE_RULES.length} types of characters: ` +
  `${CHARACTER_TYPE_RULES.map((rule) => rule.label)
    .slice(0, -1)
    .join(', ')} and ` +
  `${CHARACTER_TYPE_RULES[CHARACTER_TYPE_RULES.length - 1].label}`;

export const calculatePasswordStrength = (
  password: string
): IPasswordStrengthResult => {
  const passwordValue = password || '';
  const matchingCharacterTypes = CHARACTER_TYPE_RULES.filter((rule) =>
    rule.test(passwordValue)
  );
  const meetsPolicy =
    passwordValue.length >= MIN_PASSWORD_LENGTH &&
    matchingCharacterTypes.length >= MIN_PASSWORD_CHARACTER_TYPES;

  const zxcvbnResult = passwordValue ? zxcvbn(passwordValue) : undefined;
  const zxcvbnScore = zxcvbnResult?.score ?? 0;
  const zxcvbnSuggestions = zxcvbnResult?.feedback?.suggestions ?? [];

  const adjustedScore = meetsPolicy
    ? Math.max(zxcvbnScore, MIN_ACCEPTABLE_SCORE)
    : Math.min(zxcvbnScore, MAX_FAILING_SCORE);

  const strengthLevel = STRENGTH_LEVELS[adjustedScore] ?? STRENGTH_LEVELS[0];

  return {
    color: strengthLevel.color,
    strength: strengthLevel.percent,
    strengthText: strengthLevel.description,
    isAcceptable: meetsPolicy,
    suggestions: [
      ...(meetsPolicy ? [] : [PASSWORD_POLICY_SUGGESTION]),
      ...zxcvbnSuggestions
    ]
  };
};
